import { Text, useResponsiveValue } from 'braid-design-system';
import { useSelector } from 'react-redux';

import type { StoreProps } from 'src/store';
import type { BusinessValueData } from 'src/store/calculatorForm';
import { formatPriceBand } from 'src/utils/investmentBands';

import { FontAwesomeArrowUpSvg } from './components/FontAwesomeArrowUpSvg';

const Ruler = () => {
  const responsiveValue = useResponsiveValue();
  const isMobile = responsiveValue({
    mobile: true,
    tablet: false,
  });
  const formData = useSelector(
    ({ calculatorForm }: StoreProps) => calculatorForm,
  );
  // @ts-ignore
  const valuationData = valuation(formData);
  if (!valuationData || !valuationData.labels) {
    return (
      <div>
        <Text tone="critical" size="large">
          Please make sure you have entered values on all previous screens
        </Text>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          fontFamily:
            'Roboto,Helvetica Neue,HelveticaNeue,Helvetica,Arial,sans-serif',
          textAlign: 'center',
          fontSize: '24pt',
          color: '#0d3880',
          lineHeight: '1.6em',
        }}
      >
        Estimated value
        <br />
        {formatPriceBand(valuationData.lowestValue)} -{' '}
        {formatPriceBand(valuationData.highestValue)}
      </div>
      <div
        style={{
          textAlign: 'center',
          width: '100%',
          position: 'relative',
          left: '0px',
          whiteSpace: 'nowrap',
          marginTop: '20px',
        }}
      >
        {valuationData.labels.map((data, index) => (
          <div
            key={index}
            style={{
              width: '5%',
              textAlign: 'center',
              display: 'inline-block',
            }}
          >
            <Text size="small">
              {index % (isMobile ? 4 : 2) === 0 ? data : ' '}
            </Text>
            <br />
            <span
              style={{
                fontFamily:
                  'Roboto,Helvetica Neue,HelveticaNeue,Helvetica,Arial,sans-serif',
                fontSize: index % 2 === 0 ? '14pt' : '8pt',
                color: 'silver',
              }}
            >
              |
            </span>
          </div>
        ))}
      </div>
      <div
        style={{
          width: '50%',
          display: 'inline-block',
        }}
      >
        <div
          style={{
            width: '90%',
            float: 'right',
            backgroundImage: 'linear-gradient(to left, #0d3880 , white)',
          }}
        >
          <br />
        </div>
      </div>
      <div
        style={{
          width: '50%',
          display: 'inline-block',
        }}
      >
        <div
          style={{
            width: '90%',
            float: 'left',
            backgroundImage: 'linear-gradient(to right, #0d3880 , white)',
          }}
        >
          <br />
        </div>
      </div>
      <div
        style={{
          fontFamily:
            'Roboto,Helvetica Neue,HelveticaNeue,Helvetica,Arial,sans-serif',
          position: 'relative',
          textAlign: 'center',
          color: 'silver',
          fontSize: '18pt',
          lineHeight: '1.2em',
          marginBottom: '30px',
        }}
      >
        <span
          style={{
            fontSize: '24pt',
            color: '#0d3880',
          }}
        >
          <FontAwesomeArrowUpSvg />
        </span>
        <br />
        median value
        <br />
        {formatPriceBand(valuationData.midpoint)}
      </div>
    </div>
  );
};

export default Ruler;

const valuation = (valuationData: BusinessValueData) => {
  const upperMultiple = 3;
  const lowerMultiple = 1;
  // https://exitadviser.com/business-value.aspx?id=business-valuation-methods
  if (valuationData.profit === 0) {
    return {};
  }
  const maxBands = 20;
  const yearlyProfit = valuationData.profit * 12;
  let lowestValue = yearlyProfit * lowerMultiple;
  let highestValue = yearlyProfit * upperMultiple;
  const stockNumber = parseInt(valuationData.stock.toString(), 10);
  if (!isNaN(stockNumber)) {
    lowestValue += stockNumber;
    highestValue += stockNumber;
  }
  const difference = highestValue - lowestValue;
  let roundingValue = 10;
  let displayValue = 1;
  let gap = 250;
  let labelSuffix = '';
  if (difference > 1000000) {
    roundingValue = 100000;
    labelSuffix = 'M';
    gap = 500000;
    displayValue = 1000000;
  } else if (difference > 100000) {
    roundingValue = 10000;
    labelSuffix = 'k';
    gap = 50000;
    displayValue = 1000;
  } else if (difference > 10000) {
    roundingValue = 1000;
    labelSuffix = 'k';
    gap = 5000;
    displayValue = 1000;
  }
  lowestValue = Math.round(lowestValue / roundingValue) * roundingValue;
  highestValue = Math.round(highestValue / roundingValue) * roundingValue;
  const midpoint = (highestValue - lowestValue) / 2 + lowestValue;
  const displayMidpoint = Math.ceil(midpoint / roundingValue) * roundingValue;
  const bandsAboveAndBelow = maxBands / 2;
  const labels = [];
  for (let i = -1 * bandsAboveAndBelow; i < bandsAboveAndBelow; i++) {
    const band = (displayMidpoint + i * gap) / displayValue;
    labels.push(band + labelSuffix);
  }

  return {
    lowestValue,
    highestValue,
    midpoint,
    labels,
  };
};
